import { c as _c } from "react/compiler-runtime";
import { Github } from 'lucide-react';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { BalanceWeightFigure } from '../home/BalanceWeightFigure';
import { HomeFeatures } from '../home/HomeFeatures';
import { HomeHero } from '../home/HomeHero';
import { LoadDemoDialog } from '../home/LoadDemoDialog';
import { useHomeState } from '../home/hooks/use-home-state';
import { MainLayout } from '../MainLayout';
import { Button } from '@/components/ui/button';
import { useNewProfileDialog } from '@/contexts/new-profile-dialog-context';
import { useAutoloadDemoData } from '@/hooks/demo/use-autoload-demo-data';
import { usePianoProfileState } from '@/hooks/store/use-piano-profile-store';
import { useTranslation } from '@/hooks/use-translation';
export const Homepage = () => {
  const $ = _c(62);
  const {
    t
  } = useTranslation();
  const {
    displayName
  } = usePianoProfileState();
  const {
    openNewProfileDialog
  } = useNewProfileDialog();
  const [isDemoDialogOpen, setIsDemoDialogOpen] = useState(false);
  const home = useHomeState();
  useAutoloadDemoData();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => setIsDemoDialogOpen(true);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== home || $[2] !== openNewProfileDialog) {
    t1 = <HomeHero home={home} onCreateProfile={openNewProfileDialog} onLoadDemo={t0} />;
    $[1] = home;
    $[2] = openNewProfileDialog;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== displayName || $[5] !== home.stage || $[6] !== t) {
    t2 = home.stage === "demo" ? t("Balance weight \xB7 demonstration piano") : displayName ? t("Balance weight \xB7 your {{piano}}", {
      piano: displayName
    }) : t("Balance weight \xB7 your piano");
    $[4] = displayName;
    $[5] = home.stage;
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== home || $[9] !== t2) {
    t3 = <BalanceWeightFigure home={home} description={t2} />;
    $[8] = home;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t1 || $[12] !== t3) {
    t4 = <section className="flex flex-col items-center gap-6 border-b border-border bg-[radial-gradient(70%_80%_at_50%_0%,var(--card)_0%,transparent_70%)] px-12 pt-[104px] max-md:px-4 max-md:pt-12 [&:not(:has(>figure))]:pb-16 max-md:[&:not(:has(>figure))]:pb-10">{t1}{t3}</section>;
    $[11] = t1;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== home.measured || $[15] !== home.total || $[16] !== isDemoDialogOpen) {
    t5 = <LoadDemoDialog isOpen={isDemoDialogOpen} measured={home.measured} onOpenChange={setIsDemoDialogOpen} total={home.total} />;
    $[14] = home.measured;
    $[15] = home.total;
    $[16] = isDemoDialogOpen;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <HomeFeatures />;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="h-px flex-1 bg-input" />;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t) {
    t8 = t("Warning");
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t8) {
    t9 = <span className="ptl-label text-ptl-brass">{t8}</span>;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span className="h-px flex-1 bg-input" />;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t9) {
    t11 = <span className="flex w-full items-center gap-3.5">{t7}{t9}{t10}</span>;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t) {
    t12 = t("Piano Touch Lab is a toolbox, not a magic wand. It provides the tools for measurement, analysis and calculation, but it is no substitute for the judgement of a trained technician.");
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12) {
    t13 = <p className="m-0 font-serif text-[clamp(20px,2vw,26px)] leading-[1.45] italic">{t12}</p>;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t) {
    t14 = t("It assists technicians in their design decisions; it does not make them for them.");
    $[31] = t;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t14) {
    t15 = <p className="m-0 text-[15px] text-muted-foreground">{t14}</p>;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t11 || $[36] !== t13 || $[37] !== t15) {
    t16 = <section className="px-12 pt-20 pb-16 max-md:px-4 max-md:py-12"><aside className="mx-auto flex max-w-[760px] flex-col items-center gap-4 text-center">{t11}{t13}{t15}</aside></section>;
    $[35] = t11;
    $[36] = t13;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <em className="ptl-accent">this project</em>;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t) {
    t18 = <h2 className="m-0 flex-[1_1_260px] font-serif text-[42px] leading-[1.05] font-medium"><Trans t={t} i18nKey="About <1>this project</1>">About {t17}</Trans></h2>;
    $[40] = t;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t) {
    t19 = t("Piano Touch Lab was born from my curiosity as an amateur pianist and software developer, eager to understand the piano\u2019s mechanics in depth. In exploring the fundamentals of piano geometry and touch, I discovered the work of David C. Stanwood and created this tool to experiment with his \u201CEquation of Balance\u201D and the \u201CNew Touch Weight Metrology\u201D. What began as a way to deepen my own understanding gradually evolved into a full-fledged application, which I decided to share as open source, so that others can explore, learn, and build upon it.");
    $[42] = t;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t19) {
    t20 = <p className="m-0 text-[16.5px] leading-[1.75] text-muted-foreground">{t19}</p>;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Github className="size-4" />;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t) {
    t22 = t("View on GitHub");
    $[47] = t;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t22) {
    t23 = <Button asChild={true} variant="outlineStrong" className="self-start"><a href="https://github.com/madmatah/piano-touch-lab" target="_blank" rel="noreferrer">{t21}{t22}</a></Button>;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t20 || $[52] !== t23) {
    t24 = <div className="flex flex-[2_1_420px] flex-col gap-4">{t20}{t23}</div>;
    $[51] = t20;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t18 || $[55] !== t24) {
    t25 = <section className="px-12 pb-24 max-md:px-4 max-md:pb-16"><div className="mx-auto flex max-w-[1120px] flex-wrap gap-10 border-t border-border pt-14">{t18}{t24}</div></section>;
    $[54] = t18;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t16 || $[58] !== t25 || $[59] !== t4 || $[60] !== t5) {
    t26 = <MainLayout variant="bare" shouldDisplayDemoBanner={false}>{t4}{t5}{t6}{t16}{t25}</MainLayout>;
    $[57] = t16;
    $[58] = t25;
    $[59] = t4;
    $[60] = t5;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  return t26;
};